/* CONTEXT:START
Wave: 3
Status: implemented
Feature: Add to Report terminology - updated from approve/reject to Add to Report workflow
Dependencies: [PenholderApiService, WorkflowApiService, Cognito authentication, Updated types]
Risk-Level: medium
Validation-Method: integration-test
Performance-Target: <2s
AWS-Profile: dev
Last-Updated: 2025-08-05
CONTEXT:END */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Cancel,
  Edit,
  Schedule,
  Report
} from '@mui/icons-material';
import { fetchAuthSession } from 'aws-amplify/auth';
import { 
  Submission, 
  PenholderAssignment, 
  PenholderMetrics, 
  WorkflowState,
  SectionedSubmissions
} from '../types';
import { penholderApiService } from '../services/PenholderApiService';
import { workflowApiService } from '../services/WorkflowApiService';
import { MockPenholderDataService } from '../services/MockPenholderDataService';
import ReportGenerator from './admin/ReportGenerator';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`penholder-tabpanel-${index}`}
      aria-labelledby={`penholder-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

/**
 * Main dashboard for penholders to manage submissions and generate reports
 * Shows workflow-aligned metrics and organized submission sections by state
 * Wave 1: Removed old "Assigned Submissions" section in favor of organized workflow state sections
 */
/* CONTEXT:START
Wave: 1
Status: stub
Feature: Submissions Metrics - Updated state management for sectioned submissions
Dependencies: [SectionedSubmissions interface, Mock data service]
Risk-Level: low
Performance-Target: <2s
Last-Updated: 2025-08-06
CONTEXT:END */

export const PenholderDashboard: React.FC = () => {
  // State management
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState<PenholderAssignment | null>(null);
  const [sectionedSubmissions, setSectionedSubmissions] = useState<SectionedSubmissions | null>(null);
  const [metrics, setMetrics] = useState<PenholderMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog state for submission review
  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean;
    submission: Submission | null;
    action: 'add_to_report' | 'not_add_to_report' | 'edit' | 'exclude_from_report' | 'move_to_pending' | null;
    feedback: string;
  }>({
    open: false,
    submission: null,
    action: null,
    feedback: ''
  });

  // Load dashboard data on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get authentication token from Amplify
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        if (token) {
          // Set authentication token for API services
          penholderApiService.setAuthToken(token);
          workflowApiService.setAuthToken(token);
        }
        
        // Load dashboard data
        await loadDashboardData();
      } catch (error) {
        console.error('Authentication setup error:', error);
        setError('Authentication failed. Please sign in again.');
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /* CONTEXT:START
  Wave: 3
  Status: implemented
  Feature: Submissions Metrics - Wave 3 Complete Integration with new endpoints
  Dependencies: [PenholderApiService, New Lambda functions, API Gateway endpoints]
  Risk-Level: low
  Performance-Target: <1.5s for complete dashboard load
  Last-Updated: 2025-08-07
  CONTEXT:END */

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading dashboard data with Wave 3 complete integration...');

      // Get current assignment (keep existing logic)
      const mockAssignment: PenholderAssignment = {
        id: 'assignment-2025-08',
        penholderUserId: 'current-user',
        penholderUserName: 'Current Penholder',
        month: 8,
        year: 2025,
        assignedAt: new Date().toISOString(),
        isActive: true
      };
      setAssignment(mockAssignment);

      // Wave 3: Try to use the combined dashboard endpoint first for optimal performance
      try {
        console.log('Attempting to use combined dashboard endpoint...');
        const dashboardData = await penholderApiService.getDashboardData('current-user', 2025, 8);
        console.log('Received combined dashboard data:', dashboardData);
        
        setMetrics(dashboardData.metrics);
        setSectionedSubmissions(dashboardData.sectionedSubmissions);
        
        console.log('✅ Successfully loaded data using combined dashboard endpoint');
        return; // Success, exit early
      } catch (dashboardError) {
        console.warn('Combined dashboard endpoint failed, falling back to individual endpoints:', dashboardError);
      }

      // Wave 3: Fallback to individual endpoints if combined endpoint fails
      console.log('Using individual endpoints for metrics and sectioned submissions...');
      
      // Load metrics and sectioned submissions in parallel for better performance
      const [realMetrics, realSectionedSubmissions] = await Promise.all([
        penholderApiService.getMetrics('current-user', 2025, 8),
        penholderApiService.getSectionedSubmissions('current-user', 2025, 8)
      ]);
      
      console.log('Received metrics from individual endpoint:', realMetrics);
      console.log('Received sectioned submissions from individual endpoint:', realSectionedSubmissions);
      
      setMetrics(realMetrics);
      setSectionedSubmissions(realSectionedSubmissions);
      
      console.log('✅ Successfully loaded data using individual endpoints');

    } catch (err) {
      console.error('Dashboard data loading error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      
      // Fallback to mock data if all API calls fail
      console.warn('All API endpoints failed, falling back to mock data...');
      try {
        const fallbackMetrics = await MockPenholderDataService.getMockMetrics('current-user', 2025, 8);
        setMetrics(fallbackMetrics);
        
        const fallbackSectionedSubmissions = await MockPenholderDataService.getMockSectionedSubmissions('current-user');
        setSectionedSubmissions(fallbackSectionedSubmissions);
        
        setError('Using offline data - some features may be limited');
        console.log('✅ Successfully loaded fallback mock data');
      } catch (fallbackErr) {
        console.error('Fallback data loading failed:', fallbackErr);
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAction = (submission: Submission, action: 'add_to_report' | 'not_add_to_report' | 'edit' | 'exclude_from_report' | 'move_to_pending') => {
    setReviewDialog({
      open: true,
      submission,
      action,
      feedback: submission.penholderFeedback || ''
    });
  };

  /**
   * Handle submission review actions with proper workflow state transitions
   * BUGFIX: Handles two-step transition for 'edit' action on 'submitted' submissions
   * - From 'submitted': Must go through 'under_review' before 'edited'
   * - From 'under_review': Can go directly to 'edited'
   */
  const handleReviewSubmit = async () => {
    if (!reviewDialog.submission || !reviewDialog.action) return;

    try {
      let newState: WorkflowState;
      switch (reviewDialog.action) {
        case 'add_to_report':
          newState = 'added_to_report';
          break;
        case 'not_add_to_report':
        case 'exclude_from_report':
          newState = 'not_added_to_report';
          break;
        case 'move_to_pending':
          newState = 'under_review';
          break;
        case 'edit':
          // BUGFIX: Handle two-step transition for submissions in 'submitted' state
          // From 'submitted' we can only go to 'under_review', then to 'edited'
          if (reviewDialog.submission.workflowState === 'submitted') {
            // First transition to 'under_review'
            await workflowApiService.updateWorkflowState(
              reviewDialog.submission.id,
              'under_review',
              'Moving to review for editing'
            );
            // Then transition to 'edited' with the actual feedback
            newState = 'edited';
          } else {
            // Direct transition to 'edited' (from 'under_review' state)
            newState = 'edited';
          }
          break;
        default:
          return;
      }

      await workflowApiService.updateWorkflowState(
        reviewDialog.submission.id,
        newState,
        reviewDialog.feedback
      );

      // Refresh data
      await loadDashboardData();
      
      // Close dialog
      setReviewDialog({
        open: false,
        submission: null,
        action: null,
        feedback: ''
      });
    } catch (err) {
      console.error('Review submission error:', err);
      setError(err instanceof Error ? err.message : 'Failed to update submission');
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading penholder dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!assignment) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        You are not currently assigned as a penholder. Contact your administrator for assignment.
      </Alert>
    );
  }

  // Wave 1: Removed completionRate calculation - not used in new metrics
  // Wave 2: May add back if needed for progress tracking

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Penholder Dashboard
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {new Date(0, assignment.month - 1).toLocaleString('default', { month: 'long' })} {assignment.year}
      </Typography>

      {/* Tabs */}
      <Paper sx={{ width: '100%', mt: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="penholder dashboard tabs"
          >
            <Tab 
              label="Review Submissions" 
              id="penholder-tab-0" 
              aria-controls="penholder-tabpanel-0"
              icon={<Assignment />}
              iconPosition="start"
            />
            <Tab 
              label="Generate Report" 
              id="penholder-tab-1" 
              aria-controls="penholder-tabpanel-1"
              icon={<Report />}
              iconPosition="start"
            />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          {/* Metrics Overview */}
          {/* CONTEXT:START
          Wave: 1
          Status: stub
          Feature: Submissions Metrics - New workflow-aligned metrics display
          Dependencies: [MockPenholderDataService, Updated PenholderMetrics interface]
          Risk-Level: low
          Performance-Target: <2s
          Last-Updated: 2025-08-06
          CONTEXT:END */}
          
          {metrics && (
            <Grid container spacing={3} sx={{ mb: 4, pt: 3 }}>
              {/* Submitted Metric */}
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Assignment color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2" color="textSecondary">
                        Submitted
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {metrics.submitted}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total submissions this month
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Pending Review Metric */}
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Schedule color="warning" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2" color="textSecondary">
                        Pending Review
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {metrics.pendingReview}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Awaiting your review
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Added to Report Metric */}
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <CheckCircle color="success" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2" color="textSecondary">
                        Added to Report
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {metrics.addedToReport}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Selected for monthly report
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Not Chosen Metric */}
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Cancel color="error" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2" color="textSecondary">
                        Not Chosen for Report
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {metrics.notChosen}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Not selected for report
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Redundant Card-based submission display removed - using sectioned display below */}

          {/* CONTEXT:START
          Wave: 1
          Status: stub
          Feature: Submissions Metrics - Sectioned submission lists by workflow state
          Dependencies: [MockPenholderDataService, SectionedSubmissions interface, Material-UI components]
          Risk-Level: low
          Performance-Target: <2s
          Last-Updated: 2025-08-06
          CONTEXT:END */}

          {/* Wave 1: Cleaner Sectioned Submissions Display with State-Specific Actions */}
          {sectionedSubmissions && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Submissions by Workflow State
              </Typography>

              {/* Pending Review Section */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center">
                      <Schedule color="warning" sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        Pending Review ({sectionedSubmissions.pendingReview.length})
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${sectionedSubmissions.pendingReview.length} items`} 
                      color="warning" 
                      size="small" 
                    />
                  </Box>
                  {sectionedSubmissions.pendingReview.length > 0 ? (
                    <Grid container spacing={2}>
                      {sectionedSubmissions.pendingReview.map((submission) => (
                        <Grid item xs={12} key={submission.id}>
                          <Paper sx={{ p: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>{submission.type === 'highlight' ? '✨' : '⚠️'} {submission.type.toUpperCase()}</strong>
                            </Typography>
                            <Typography variant="body1">
                              {submission.content}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Submitted by: {submission.userName || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Submitted: {new Date(submission.createdAt).toLocaleDateString()}
                            </Typography>
                            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                              {/* State-specific actions for Pending Review */}
                              <Button 
                                size="small" 
                                variant="outlined" 
                                startIcon={<Edit />}
                                onClick={() => handleReviewAction(submission, 'edit')}
                                disabled={loading}
                              >
                                Edit
                              </Button>
                              <Button 
                                size="small" 
                                variant="contained" 
                                color="success" 
                                startIcon={<CheckCircle />}
                                onClick={() => handleReviewAction(submission, 'add_to_report')}
                                disabled={loading}
                              >
                                Add to Report
                              </Button>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="error" 
                                startIcon={<Cancel />}
                                onClick={() => handleReviewAction(submission, 'exclude_from_report')}
                                disabled={loading}
                              >
                                Exclude from Report
                              </Button>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No submissions pending review
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Added to Report Section */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center">
                      <CheckCircle color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        Added to Report ({sectionedSubmissions.addedToReport.length})
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${sectionedSubmissions.addedToReport.length} items`} 
                      color="success" 
                      size="small" 
                    />
                  </Box>
                  {sectionedSubmissions.addedToReport.length > 0 ? (
                    <Grid container spacing={2}>
                      {sectionedSubmissions.addedToReport.map((submission) => (
                        <Grid item xs={12} key={submission.id}>
                          <Paper sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>{submission.type === 'highlight' ? '✨' : '⚠️'} {submission.type.toUpperCase()}</strong>
                            </Typography>
                            <Typography variant="body1">
                              {submission.content}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Submitted by: {submission.userName || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Added to report: {submission.addedToReportAt ? new Date(submission.addedToReportAt).toLocaleDateString() : 'Recently'}
                            </Typography>
                            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                              {/* State-specific actions for Added to Report */}
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="warning" 
                                startIcon={<Schedule />}
                                onClick={() => handleReviewAction(submission, 'move_to_pending')}
                                disabled={loading}
                              >
                                Move to Pending Review
                              </Button>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="error" 
                                startIcon={<Cancel />}
                                onClick={() => handleReviewAction(submission, 'exclude_from_report')}
                                disabled={loading}
                              >
                                Exclude from Report
                              </Button>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No submissions added to report yet
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Not Chosen for Report Section */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center">
                      <Cancel color="error" sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        Not Chosen for Report ({sectionedSubmissions.notChosen.length})
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${sectionedSubmissions.notChosen.length} items`} 
                      color="error" 
                      size="small" 
                    />
                  </Box>
                  {sectionedSubmissions.notChosen.length > 0 ? (
                    <Grid container spacing={2}>
                      {sectionedSubmissions.notChosen.slice(0, 5).map((submission) => (
                        <Grid item xs={12} key={submission.id}>
                          <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>{submission.type === 'highlight' ? '✨' : '⚠️'} {submission.type.toUpperCase()}</strong>
                            </Typography>
                            <Typography variant="body1">
                              {submission.content}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Submitted by: {submission.userName || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Excluded: {submission.reportExclusionReason || 'Not selected for report'}
                            </Typography>
                            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                              {/* State-specific actions for Not Chosen */}
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="warning" 
                                startIcon={<Schedule />}
                                onClick={() => handleReviewAction(submission, 'move_to_pending')}
                                disabled={loading}
                              >
                                Move to Pending Review
                              </Button>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="success" 
                                startIcon={<CheckCircle />}
                                onClick={() => handleReviewAction(submission, 'add_to_report')}
                                disabled={loading}
                              >
                                Add to Report
                              </Button>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                      {sectionedSubmissions.notChosen.length > 5 && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary" align="center">
                            ... and {sectionedSubmissions.notChosen.length - 5} more items
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No submissions marked as not chosen
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Report Generation Authority:</strong> As the assigned penholder, you have the authority to generate the monthly report using submissions you've added to the report. This replaces the previous admin-only report generation process.
            </Typography>
          </Alert>
          <ReportGenerator />
        </TabPanel>
      </Paper>

      {/* Review Dialog */}
      <Dialog 
        open={reviewDialog.open} 
        onClose={() => setReviewDialog({ ...reviewDialog, open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {reviewDialog.action === 'add_to_report' && 'Add Submission to Report'}
          {reviewDialog.action === 'not_add_to_report' && 'Don\'t Add to Report'}
          {reviewDialog.action === 'exclude_from_report' && 'Exclude from Report'}
          {reviewDialog.action === 'move_to_pending' && 'Move to Pending Review'}
          {reviewDialog.action === 'edit' && 'Edit Submission'}
        </DialogTitle>
        <DialogContent>
          {reviewDialog.submission && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Original Content:
              </Typography>
              <Typography variant="body2" paragraph sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                {reviewDialog.submission.content}
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Feedback / Comments"
                value={reviewDialog.feedback}
                onChange={(e) => setReviewDialog({ ...reviewDialog, feedback: e.target.value })}
                placeholder={
                  reviewDialog.action === 'add_to_report' 
                    ? 'Optional: Add reason for including in report...'
                    : reviewDialog.action === 'not_add_to_report' || reviewDialog.action === 'exclude_from_report'
                    ? 'Required: Explain why this submission is not being added to report...'
                    : reviewDialog.action === 'move_to_pending'
                    ? 'Optional: Add reason for moving back to pending review...'
                    : 'Required: Provide editing suggestions...'
                }
                required={reviewDialog.action !== 'add_to_report' && reviewDialog.action !== 'move_to_pending'}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog({ ...reviewDialog, open: false })}>
            Cancel
          </Button>
          <Button 
            onClick={handleReviewSubmit}
            variant="contained"
            disabled={
              (reviewDialog.action !== 'add_to_report' && reviewDialog.action !== 'move_to_pending') && 
              !reviewDialog.feedback.trim()
            }
          >
            {reviewDialog.action === 'add_to_report' && 'Add to Report'}
            {reviewDialog.action === 'not_add_to_report' && 'Don\'t Add'}
            {reviewDialog.action === 'exclude_from_report' && 'Exclude'}
            {reviewDialog.action === 'move_to_pending' && 'Move to Pending'}
            {reviewDialog.action === 'edit' && 'Save Feedback'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
